import { FunctionComponent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faMagnifyingGlass, faTableCells } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Modal } from "@/components/Modals/Modal";
import { classNames } from "@/utils/classNames";

import {
	getSourceFilterGroups,
	getSourceIconClasses,
	SourceFilterEntry,
	sourceMatchesQuery,
} from "../config/sourceTypeFilterCatalog";

/** Sentinel id for the "no source filter" option. Matches the overview's existing `ALL` value. */
export const ALL_SOURCES = "ALL";

interface SourceTypePaletteProps {
	isOpen: boolean;
	/** Currently applied source filter (`ALL`/undefined means no filter). */
	selectedSourceType?: string;
	onSelect: (id: string) => void;
	onClose: () => void;
}

interface FlatItem {
	id: string;
	label: string;
	description: string;
	entry?: SourceFilterEntry;
}

export const SourceTypePalette: FunctionComponent<SourceTypePaletteProps> = ({
	isOpen,
	selectedSourceType,
	onSelect,
	onClose,
}) => {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [highlight, setHighlight] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

	const isAllSelected = !selectedSourceType || selectedSourceType === ALL_SOURCES;

	// Resolve labels/descriptions once and apply the text search. Empty groups drop out so the
	// palette only ever shows categories that still have a match.
	const groups = useMemo(() => {
		return getSourceFilterGroups()
			.map((group) => ({
				...group,
				entries: group.entries.filter((entry) =>
					sourceMatchesQuery(entry, t(entry.labelKey), t(entry.descriptionKey), query)
				),
			}))
			.filter((group) => group.entries.length > 0);
	}, [query, t]);

	// Flat, in-display-order list of selectable rows — drives keyboard navigation. The "All
	// sources" row is only offered when the user hasn't typed a query.
	const flatItems = useMemo<FlatItem[]>(() => {
		const items: FlatItem[] = [];

		if (!query.trim()) {
			items.push({
				id: ALL_SOURCES,
				label: t("list.sourceFilter.all"),
				description: t("list.sourceFilter.allDescription"),
			});
		}

		groups.forEach((group) => {
			group.entries.forEach((entry) => {
				items.push({
					id: entry.id,
					label: t(entry.labelKey),
					description: t(entry.descriptionKey),
					entry,
				});
			});
		});

		return items;
	}, [groups, query, t]);

	// Reset transient UI state whenever the palette opens, and focus the search field.
	useEffect(() => {
		if (isOpen) {
			setQuery("");
			setHighlight(0);
			const id = window.setTimeout(() => inputRef.current?.focus(), 50);

			return () => window.clearTimeout(id);
		}
	}, [isOpen]);

	// Keep the highlight in range as the result set shrinks/grows, and scroll it into view.
	useEffect(() => {
		setHighlight((current) => Math.min(current, Math.max(flatItems.length - 1, 0)));
	}, [flatItems.length]);

	useEffect(() => {
		itemRefs.current[highlight]?.scrollIntoView?.({ block: "nearest" });
	}, [highlight]);

	const handleSelect = (id: string) => {
		onSelect(id);
		onClose();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlight((current) => Math.min(current + 1, flatItems.length - 1));
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlight((current) => Math.max(current - 1, 0));
		} else if (event.key === "Enter") {
			event.preventDefault();
			const item = flatItems[highlight];

			if (item) {
				handleSelect(item.id);
			}
		}
	};

	const renderIcon = (entry?: SourceFilterEntry) => {
		if (!entry) {
			return <Icon className="h-[18px] w-[18px]" icon={faTableCells} />;
		}

		if (entry.icon) {
			return <SvgIcon className="h-[18px] w-[18px]" svgIcon={entry.icon} />;
		}

		if (entry.secondIcon) {
			return <Icon className="h-[18px] w-[18px]" icon={entry.secondIcon} />;
		}

		return null;
	};

	const renderCard = (item: FlatItem, index: number) => {
		const isSelected = item.id === ALL_SOURCES ? isAllSelected : item.id === selectedSourceType;
		const isHighlighted = index === highlight;

		return (
			<button
				key={item.id}
				ref={(element) => {
					itemRefs.current[index] = element;
				}}
				className={classNames(
					"group flex w-full cursor-pointer items-center gap-3 rounded-[10px] border bg-white p-3 text-left transition duration-150",
					isSelected ? "border-blue-action bg-selected-item" : "border-border-light",
					isHighlighted ? "ring-2 ring-blue-action" : "hover:border-blue-action hover:shadow-sm"
				)}
				type="button"
				onClick={() => handleSelect(item.id)}
				onMouseEnter={() => setHighlight(index)}
			>
				<span
					className={classNames(
						"flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
						item.entry
							? getSourceIconClasses(item.entry.iconVariant)
							: "bg-blue-action-200 text-blue-action"
					)}
				>
					{renderIcon(item.entry)}
				</span>
				<span className="min-w-0 flex-1">
					<span className="block truncate text-sm font-semibold text-text">{item.label}</span>
					<span className="block truncate text-xs leading-5 text-text-meta">
						{item.description}
					</span>
				</span>
				{isSelected && <Icon className="h-4 w-4 shrink-0 text-blue-action" icon={faCheck} />}
			</button>
		);
	};

	// Map each flat-item id back to its keyboard index so cards stay navigable across groups.
	const indexOfId = (id: string) => flatItems.findIndex((item) => item.id === id);

	return (
		<Modal
			isOpened={isOpen}
			size="md"
			title={t("list.sourceFilter.title")}
			footer={
				<>
					<span className="text-xs text-text-meta">{t("list.sourceFilter.footerHint")}</span>
					<span className="text-xs text-text-disabled">
						{t("list.sourceFilter.count", { count: flatItems.filter((i) => i.entry).length })}
					</span>
				</>
			}
			handleClose={onClose}
		>
			<div className="flex flex-col">
				<div className="flex items-center gap-3 rounded-[10px] border border-border-light bg-white px-3 py-2.5 focus-within:border-blue-action focus-within:shadow-sm">
					<Icon className="h-4 w-4 shrink-0 text-text-meta" icon={faMagnifyingGlass} />
					<input
						ref={inputRef}
						className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-disabled"
						placeholder={t("list.sourceFilter.searchPlaceholder")}
						type="text"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={handleKeyDown}
					/>
					<button
						className="shrink-0 cursor-pointer rounded border border-border-light px-1.5 py-0.5 text-xxs text-text-disabled transition hover:text-text-meta"
						type="button"
						onClick={onClose}
					>
						esc
					</button>
				</div>

				<div className="mt-3 max-h-[52vh] overflow-y-auto px-1 py-1">
					{!query.trim() && <div className="mb-3">{renderCard(flatItems[0], 0)}</div>}

					{flatItems.length === 0 && (
						<div className="rounded-[10px] border border-border-light bg-white p-4 text-center text-sm text-text-meta">
							{t("list.sourceFilter.empty")}
						</div>
					)}

					{groups.map((group) => (
						<div key={group.category} className="mb-4">
							<div className="mb-2 px-1 text-xxs font-bold uppercase tracking-wide text-text-meta">
								{t(group.labelKey)}
							</div>
							<div className="grid grid-cols-1 gap-2 tb:grid-cols-2">
								{group.entries.map((entry) => {
									const index = indexOfId(entry.id);

									return renderCard(flatItems[index], index);
								})}
							</div>
						</div>
					))}
				</div>
			</div>
		</Modal>
	);
};
